/**
 * US-035 — Rule bundle library hooks.
 *
 * Two surfaces:
 *   - Platform admin (CRUD + publish + new-version + archive + items)
 *     under `/api/admin/rule-bundles/...`. Query keys
 *     `["admin", "rule-bundles", ...]`.
 *   - Group home admin (browse + import) under `/api/rule-bundles/...`
 *     and `/api/group-homes/:id/import-rule-bundle`. Query keys
 *     `["rule-bundles", ...]`.
 *
 * Wire shapes mirror the backend: `RuleBundle` and `RuleBundleItem` use
 * snake_case to match the API responses (no camelCase translation layer).
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../lib/apiClient";

export type RuleBundleStatus = "draft" | "published" | "archived";
export type RuleBundleMode = "attach_only" | "auto_issue";

export interface RuleBundle {
  id: string;
  code: string;
  display_name: string;
  description: string;
  state: string | null;
  specialty: string | null;
  regulatory_citations: string[];
  version: number;
  parent_bundle_id: string | null;
  is_latest_version: boolean;
  published_at: string | null;
  status: RuleBundleStatus;
  // null = system-authored (platform baseline seeded by the boot-time registry sync).
  created_by: string | null;
  created_at: string;
  updated_at: string;
  _count?: { items: number };
}

export type RuleBundleItemKind =
  | "appointment_form_rule"
  | "resident_form_requirement";

export interface RuleBundleItem {
  id: string;
  bundle_id: string;
  kind: RuleBundleItemKind;
  form_template_id: string;
  is_required: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
  // appointment_form_rule fields (null on resident_form_requirement rows)
  appointment_type_code: string | null;
  appointment_type_display_name: string | null;
  appointment_type_description: string | null;
  mode: RuleBundleMode | null;
  default_expires_after_hours: number | null;
  // resident_form_requirement fields (null on appointment_form_rule rows)
  resident_frequency: string | null;
  resident_reason_template: string | null;
  resident_due_anchor: string | null;
  resident_due_offset_days: number | null;
  // US-114: composite cadence the one_shot transitions to on first fulfillment.
  resident_recurring_frequency: string | null;
  resident_default_duration_days: number | null;
  form_template?: {
    id: string;
    name: string;
    status: string;
    audience?: string;
    // external_state_form templates link to the state-issued PDF.
    external_form_url?: string | null;
    external_form_label?: string | null;
    signature_mode?: string;
  };
}

export interface RuleBundleWithItems extends RuleBundle {
  items: RuleBundleItem[];
}

export interface CreateRuleBundlePayload {
  code: string;
  display_name: string;
  description: string;
  state?: string | null;
  specialty?: string | null;
  regulatory_citations?: string[];
}

export interface UpdateRuleBundlePayload {
  display_name?: string;
  description?: string;
  state?: string | null;
  specialty?: string | null;
  regulatory_citations?: string[];
}

export interface CreateAppointmentRuleItemPayload {
  kind?: "appointment_form_rule";
  appointment_type_code: string;
  appointment_type_display_name: string;
  appointment_type_description?: string | null;
  form_template_id: string;
  is_required?: boolean;
  mode?: RuleBundleMode;
  default_expires_after_hours?: number | null;
  display_order?: number;
}

export interface CreateResidentRequirementItemPayload {
  kind: "resident_form_requirement";
  form_template_id: string;
  resident_frequency: string;
  resident_reason_template?: string | null;
  resident_default_duration_days?: number | null;
  resident_due_anchor?: string | null;
  resident_due_offset_days?: number | null;
  // US-114: composite cadence (only valid alongside resident_frequency 'one_shot').
  resident_recurring_frequency?: string | null;
  is_required?: boolean;
  display_order?: number;
}

export type CreateRuleBundleItemPayload =
  | CreateAppointmentRuleItemPayload
  | CreateResidentRequirementItemPayload;

export interface UpdateRuleBundleItemPayload {
  // Common
  is_required?: boolean;
  display_order?: number;
  // appointment_form_rule
  appointment_type_display_name?: string;
  appointment_type_description?: string | null;
  mode?: RuleBundleMode;
  default_expires_after_hours?: number | null;
  // resident_form_requirement
  resident_frequency?: string;
  resident_reason_template?: string | null;
}

export interface ImportRuleBundlePayload {
  bundle_id: string;
  idempotency_key: string;
  scope_facility_id?: string | null;
}

export interface ImportRuleBundleResult {
  importId: string;
  bundleVersion: number;
  createdDefinitionIds: string[];
  linkedDefinitionIds: string[];
  createdRuleIds: string[];
  skippedRules: Array<{
    appointment_type_code: string;
    form_template_id: string;
    reason: "active_rule_already_exists";
  }>;
  idempotent_replay: boolean;
}

export interface AdminBundleFilters {
  status?: RuleBundleStatus;
  state?: string;
  specialty?: string;
  search?: string;
}

export interface GroupHomeBundleFilters {
  state?: string;
  specialty?: string;
  search?: string;
}

// ── Admin: list + detail ──────────────────────────────────────────────────

function buildQueryString(
  filters: Record<string, string | undefined | null>,
): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v) params.set(k, v);
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

export function useAdminRuleBundles(filters: AdminBundleFilters = {}) {
  return useQuery<RuleBundle[]>({
    queryKey: ["admin", "rule-bundles", filters],
    queryFn: async () => {
      const { data } = await api.get(
        `/admin/rule-bundles${buildQueryString(filters as Record<string, string | undefined>)}`,
      );
      return data.data;
    },
  });
}

export function useAdminRuleBundle(id: string | undefined) {
  return useQuery<RuleBundleWithItems>({
    queryKey: ["admin", "rule-bundles", id],
    queryFn: async () => {
      const { data } = await api.get(`/admin/rule-bundles/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

// ── Admin: mutations ──────────────────────────────────────────────────────

function invalidateAdmin(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["admin", "rule-bundles"] });
}
function invalidateGroupHome(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["rule-bundles"] });
}

export function useCreateRuleBundle() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorFallback: "Failed to create bundle." },
    mutationFn: async (payload: CreateRuleBundlePayload) => {
      const { data } = await api.post("/admin/rule-bundles", payload);
      return data.data as RuleBundle;
    },
    onSuccess: () => invalidateAdmin(qc),
  });
}

export function useUpdateRuleBundle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    // US-081: error fallback centralized here; admin rule-library pages catch is sole caller.
    meta: { errorFallback: "Failed to update rule bundle" },
    mutationFn: async (payload: UpdateRuleBundlePayload) => {
      const { data } = await api.put(`/admin/rule-bundles/${id}`, payload);
      return data.data as RuleBundle;
    },
    onSuccess: () => {
      invalidateAdmin(qc);
      qc.invalidateQueries({ queryKey: ["admin", "rule-bundles", id] });
    },
  });
}

export function usePublishRuleBundle() {
  const qc = useQueryClient();
  return useMutation({
    // US-081: error fallback centralized here; admin rule-library pages catch is sole caller.
    meta: { errorFallback: "Failed to publish rule bundle" },
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/admin/rule-bundles/${id}/publish`);
      return data.data as RuleBundle;
    },
    onSuccess: (_data, id) => {
      invalidateAdmin(qc);
      invalidateGroupHome(qc);
      qc.invalidateQueries({ queryKey: ["admin", "rule-bundles", id] });
    },
  });
}

export function useCreateNewRuleBundleVersion() {
  const qc = useQueryClient();
  return useMutation({
    // US-081: error fallback centralized here; admin rule-library pages catch is sole caller.
    meta: { errorFallback: "Failed to create new version" },
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/admin/rule-bundles/${id}/new-version`);
      return data.data as RuleBundle;
    },
    onSuccess: () => invalidateAdmin(qc),
  });
}

export function useArchiveRuleBundle() {
  const qc = useQueryClient();
  return useMutation({
    // US-081: error fallback centralized here; admin rule-library pages catch is sole caller.
    meta: { errorFallback: "Failed to archive rule bundle" },
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/admin/rule-bundles/${id}/archive`);
      return data.data as RuleBundle;
    },
    onSuccess: (_data, id) => {
      invalidateAdmin(qc);
      invalidateGroupHome(qc);
      qc.invalidateQueries({ queryKey: ["admin", "rule-bundles", id] });
    },
  });
}

export function useAddRuleBundleItem(bundleId: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorFallback: "Save failed." },
    mutationFn: async (payload: CreateRuleBundleItemPayload) => {
      const { data } = await api.post(
        `/admin/rule-bundles/${bundleId}/items`,
        payload,
      );
      return data.data as RuleBundleItem;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "rule-bundles", bundleId] });
      invalidateAdmin(qc);
    },
  });
}

export function useUpdateRuleBundleItem(itemId: string, bundleId: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorFallback: "Save failed." },
    mutationFn: async (payload: UpdateRuleBundleItemPayload) => {
      const { data } = await api.put(
        `/admin/rule-bundle-items/${itemId}`,
        payload,
      );
      return data.data as RuleBundleItem;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "rule-bundles", bundleId] });
    },
  });
}

export function useRemoveRuleBundleItem(itemId: string, bundleId: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorFallback: "Remove failed." },
    mutationFn: async () => {
      const { data } = await api.delete(`/admin/rule-bundle-items/${itemId}`);
      return data.data as { deleted: true };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "rule-bundles", bundleId] });
    },
  });
}

// ── Group home: browse + detail + import ──────────────────────────────────

export function useRuleBundles(filters: GroupHomeBundleFilters = {}) {
  return useQuery<RuleBundle[]>({
    queryKey: ["rule-bundles", filters],
    queryFn: async () => {
      const { data } = await api.get(
        `/rule-bundles${buildQueryString(filters as Record<string, string | undefined>)}`,
      );
      return data.data;
    },
  });
}

export function useRuleBundle(id: string | undefined) {
  return useQuery<RuleBundleWithItems>({
    queryKey: ["rule-bundles", id],
    queryFn: async () => {
      const { data } = await api.get(`/rule-bundles/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

export function useImportRuleBundle(groupHomeId: string) {
  const qc = useQueryClient();
  return useMutation({
    // US-081: error fallback centralized here; RuleLibraryDetailModal catch is sole caller.
    meta: { errorFallback: "Import failed" },
    mutationFn: async (payload: ImportRuleBundlePayload) => {
      const { data } = await api.post(
        `/group-homes/${groupHomeId}/import-rule-bundle`,
        payload,
      );
      return data.data as ImportRuleBundleResult;
    },
    onSuccess: () => {
      // Invalidate the downstream surfaces an import affects so the
      // existing US-032 pages refresh.
      qc.invalidateQueries({ queryKey: ["appointment-form-rules"] });
      qc.invalidateQueries({ queryKey: ["appointment-type-definitions"] });
      qc.invalidateQueries({ queryKey: ["form-templates"] });
    },
  });
}
